import React, { useState, useEffect } from "react";
import {
  Typography,
  Checkbox,
  Button,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoIcon from "@mui/icons-material/Info";
import WeekendIcon from "@mui/icons-material/Weekend";
import WorkIcon from "@mui/icons-material/Work";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchDayMappings,
  updateDayMappings,
  resetDayMappings,
} from "../../../dux/dayMappings";
import styles from "./DayMapping.module.css";

const DayMapping = ({ sport, location }) => {
  const dispatch = useDispatch();
  const { dayMappings, loading, error } = useSelector(
    (state) => state.dayMappings
  );
  const [localDayMappings, setLocalDayMappings] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: true, // Default to true for weekend
    sunday: true, // Default to true for weekend
  });
  const [message, setMessage] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Load day mappings from API
  useEffect(() => {
    if (location) {
      setIsInitialLoading(true);
      dispatch(fetchDayMappings(location));
    }
  }, [dispatch, location]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (dayMappings) {
      setLocalDayMappings(dayMappings);
      setIsInitialLoading(false);
    }
  }, [dayMappings]);

  // Show error message if any
  useEffect(() => {
    if (error) {
      setMessage({
        type: "error",
        text: error,
      });
    } else {
      setMessage(null);
    }
  }, [error]);

  const handleDayToggle = (day) => {
    setLocalDayMappings((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleSave = () => {
    setMessage(null);
    dispatch(updateDayMappings(location, localDayMappings));
  };

  const handleReset = () => {
    setMessage(null);
    setIsInitialLoading(true);
    dispatch(resetDayMappings(location));
  };

  const getActiveDays = () => {
    return Object.entries(localDayMappings)
      .filter(([day, isActive]) => isActive)
      .map(([day]) => day);
  };

  const activeDays = getActiveDays();

  const isWeekend = (day) => localDayMappings[day] === true;

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <Typography variant="h4" gutterBottom className={styles.title}>
          Day Mapping Configuration
        </Typography>
        <div className={styles.headerInfo}>
          <InfoIcon color="action" fontSize="small" />
          <Typography
            variant="body1"
            color="text.secondary"
            style={{ fontStyle: "italic" }}
          >
            Select days that should automatically use holiday rates
          </Typography>
        </div>
      </div>

      {/* Alert Message */}
      {message && (
        <Alert severity={message.type} className={styles.alert}>
          {message.text}
        </Alert>
      )}

      {/* Days Selection Grid */}
      <Paper elevation={2} className={styles.daysContainer}>
        <Typography variant="h6" gutterBottom className={styles.daysTitle}>
          Select Holiday Days
        </Typography>

        <div className={styles.grid}>
          {loading || isInitialLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
                gap: 2,
                width: "100%",
              }}
            >
              <CircularProgress size={40} />
              <div style={{ color: "#666", fontSize: "16px" }}>
                {loading ? "Loading day mappings..." : "Initializing..."}
              </div>
            </Box>
          ) : (
            Object.entries(localDayMappings).map(([day, isActive]) => (
              <div key={day} className={styles.card}>
                <Paper
                  elevation={isActive ? 4 : 1}
                  className={`${styles.paper} ${
                    isActive ? styles.paperActive : styles.paperInactive
                  }`}
                  onClick={() => handleDayToggle(day)}
                >
                  <div className={styles.iconContainer}>
                    {isWeekend(day) ? (
                      <WeekendIcon color={isActive ? "primary" : "action"} />
                    ) : (
                      <WorkIcon color={isActive ? "primary" : "action"} />
                    )}
                  </div>
                  <Typography
                    variant="h6"
                    className={`${styles.dayName} ${
                      isActive ? styles.dayNameActive : ""
                    }`}
                  >
                    {day}
                  </Typography>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDayToggle(day);
                    }}
                    className={styles.checkboxContainer}
                  >
                    <Checkbox
                      checked={isActive}
                      color="primary"
                      style={{
                        "&.Mui-checked": {
                          color: "#09213c",
                        },
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </Paper>
              </div>
            ))
          )}
        </div>
      </Paper>

      {/* Active Days Display */}
      {activeDays.length > 0 && (
        <Paper elevation={2} className={styles.activeDaysContainer}>
          <Typography
            variant="h6"
            gutterBottom
            className={styles.activeDaysTitle}
          >
            Active Holiday Days ({activeDays.length})
          </Typography>
          <div className={styles.chipsContainer}>
            {activeDays.map((day) => (
              <Chip
                key={day}
                label={day.charAt(0).toUpperCase() + day.slice(1)}
                color="primary"
                size="medium"
                className={styles.chip}
              />
            ))}
          </div>
        </Paper>
      )}

      {/* Action Buttons */}
      <div className={styles.buttonContainer}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || isInitialLoading}
          className={styles.saveButton}
        >
          {loading ? "Saving..." : "Save Mappings"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={loading || isInitialLoading}
          startIcon={
            isInitialLoading ? (
              <CircularProgress size={20} />
            ) : (
              <RestartAltIcon />
            )
          }
          className={styles.resetButton}
        >
          Reset to Default
        </Button>
      </div>

      {/* Info Note */}
      <div className={styles.infoNote}>
        <Typography
          variant="body2"
          color="text.secondary"
          className={styles.infoText}
        >
          💡 This configuration is stored in the database and shared across all
          admin users. Days marked as holidays will automatically use holiday
          pricing rates for bookings.
        </Typography>
      </div>
    </div>
  );
};

export default DayMapping;
